import { useCallback } from "react"
import { useDispatch, useSelector } from "react-redux"
import { fetchAuthSession } from 'aws-amplify/auth'

import { postAnalyticsEvent } from "@/store/actions/event"
import { FILTER_LEASE_TYPES_PERSONAL, constants } from "@/lib/constants"
import { addVatToPrice, calculateTotalDealCost, driveTypeAbbreviation } from "@/lib/utilities/search"
import { VehiclePrice } from "@/types/vehicle"
import { selectIsAuthenticated } from "@/store/selectors/user"
import {
  addPriceAlertService,
  getActivePriceAlertsService,
  removePriceAlertService,
  getExpiredPriceAlertsService,
} from "@/lib/services/userService"
import { addLoader, removeLoader } from "@/store/reducers/loader"
import { addError, removeError } from "@/store/reducers/error"
import { LOADERS } from "@/lib/loaders"
import { ERRORS } from "@/lib/errors"
import { selectPriceAlertList } from "@/store/selectors/locoSpace"
import {
  addPriceAlertList,
  addPriceAlertWithDealsAndVehicleList,
  addExpiredPriceAlertWithDealsAndVehicleList,
  removePriceAlert,
  addExpiredPriceAlertList,
} from "@/store/reducers/locoSpace"
import { getDealsForPriceAlertService } from "@/lib/services/searchService"
import { PriceAlert } from "@/types/garage"

export type UsePriceAlertProps = {
  payload: PayloadProps
  selectedVehiclePrice: VehiclePrice | null
}

export type PayloadProps = {
  vehicleId: number
  terms: number[]
  mileages: number[]
  leaseTypes: number[]
  initialPaymentsInMonths: number[]
}

export const usePriceAlert = () => {
  const dispatch = useDispatch()
  const isAuthenticated = useSelector(selectIsAuthenticated)
  const priceAlertList = useSelector(selectPriceAlertList)

  const getActivePriceAlerts = useCallback(async () => {
    const LOADER = LOADERS.fetchingPriceAlerts
    const ERROR = ERRORS.fetchingPriceAlerts

    await fetchAuthSession()
    .then(async (data) => {
      const token = data.tokens?.idToken?.toString()

      await getActivePriceAlertsService({
        token,
        pageNumber: 1,
        pageSize: 10,
      })
        .then(response => {
          dispatch(addPriceAlertList(response.items))
          dispatch(removeLoader(LOADER))
        })
        .catch(() => {
          dispatch(removeLoader(LOADER))
          dispatch(addError({
            key: ERROR,
            message: constants.errorMessages.fetchingPriceAlerts,
          }))
        })
    })
    .catch(() => {
      dispatch(removeLoader(LOADER))
      dispatch(addError({
        key: ERROR,
        message: constants.errorMessages.userCredentials,
      }))
    })
  }, [dispatch])

  const getExpiredPriceAlerts = useCallback(async () => {
    const LOADER = LOADERS.fetchingPriceAlerts
    const ERROR = ERRORS.fetchingPriceAlerts

    await fetchAuthSession()
    .then(async (data) => {
      const token = data.tokens?.idToken?.toString()

      await getExpiredPriceAlertsService({
        token,
        pageNumber: 1,
        pageSize: 10,
      })
        .then(response => {
          dispatch(addExpiredPriceAlertList(response.items))
          dispatch(removeLoader(LOADER))
        })
        .catch(() => {
          dispatch(removeLoader(LOADER))
          dispatch(addError({
            key: ERROR,
            message: constants.errorMessages.fetchingPriceAlerts,
          }))
        })
    })
    .catch(() => {
      dispatch(removeLoader(LOADER))
      dispatch(addError({
        key: ERROR,
        message: constants.errorMessages.userCredentials,
      }))
    })
  }, [dispatch])

  const doesPriceAlertExist = useCallback((vehiclePrice: VehiclePrice) => {
    let priceAlert: PriceAlert[] = []

    if (isAuthenticated) {
      priceAlert = priceAlertList.filter((item: PriceAlert) => item.vehicleId === vehiclePrice.vehicleId)

      return priceAlert.length > 0 ? priceAlert[0] : false
    }

    return false
  }, [isAuthenticated, priceAlertList])

  const priceAlertDifferencesToCurrentLeaseProfile = useCallback((vehiclePrice: VehiclePrice, priceAlert: PriceAlert) => {
    const differences = {}

    if (!priceAlert.mileage.includes(vehiclePrice.mileage)) {
      differences.mileage = vehiclePrice.mileage
    }

    if (!priceAlert.leaseType.includes(vehiclePrice.leaseType)) {
      differences.leaseType = vehiclePrice.leaseType
    }

    if (!priceAlert.initialPaymentInMonths.includes(vehiclePrice.initialPaymentInMonths)) {
      differences.initialPaymentInMonths = vehiclePrice.initialPaymentInMonths
    }

    if (!priceAlert.term.includes(vehiclePrice.term)) {
      differences.term = vehiclePrice.term
    }

    return Object.keys(differences).length > 0 ? differences : null
  }, [])

  const onCreatePriceAlert = useCallback(async ({ payload, selectedVehiclePrice }: UsePriceAlertProps) => {
    const LOADER = LOADERS.addingPriceAlert
    const ERROR = ERRORS.addingPriceAlert

    if (isAuthenticated) {
      dispatch(addLoader(LOADER))
      dispatch(removeError(ERROR))

      await fetchAuthSession()
        .then(data => {
          const token = data.tokens?.idToken?.toString()

          addPriceAlertService({
            ...payload,
            token,
          })
          .then(() => {
            const includeVat = selectedVehiclePrice && selectedVehiclePrice.leaseType === FILTER_LEASE_TYPES_PERSONAL

            dispatch(
              postAnalyticsEvent({
                event: constants.eventTypes.createPriceAlert,
              }),
            )

            /* eslint-disable camelcase */
            if (typeof window.fbq !== 'undefined' && selectedVehiclePrice) {
              window.fbq('track', 'AddToWishlist', {
                content_type: 'vehicle_offer',
                content_ids: `${selectedVehiclePrice.id}`,
                value: 1,
                make: `${selectedVehiclePrice.manufacturerName}`,
                model: `${selectedVehiclePrice.modelName}`,
                trim: `${selectedVehiclePrice.derivativeName}`,
                transmission: `${selectedVehiclePrice.transmissionTypeName}`,
                body_style: `${selectedVehiclePrice.bodyStyleName}`,
                fuel_type: `${selectedVehiclePrice.fuelTypeName}`,
                drivetrain: `${driveTypeAbbreviation(selectedVehiclePrice.driveTypeName)}`,
                state_of_vehicle: 'New',
                country: 'United Kingdom',
                price: `${
                  includeVat
                    ? addVatToPrice(selectedVehiclePrice.monthlyPayment)
                    : (selectedVehiclePrice.monthlyPayment).toFixed(2)
                }`,
                currency: 'GBP',
              })
            }

            if (typeof window.ttq !== 'undefined' && selectedVehiclePrice) {
              window.ttq.track('AddToWishlist', {
                content_id: `${selectedVehiclePrice.id}`,
                content_type: 'product',
                quantity: 1,
                value: `${
                  includeVat
                    ? addVatToPrice(calculateTotalDealCost(selectedVehiclePrice))
                    : calculateTotalDealCost(selectedVehiclePrice)
                }`,
                price: `${
                  includeVat
                    ? addVatToPrice(selectedVehiclePrice.monthlyPayment)
                    : (selectedVehiclePrice.monthlyPayment).toFixed(2)
                }`,
                currency: 'GBP',
              })
            }
            getActivePriceAlerts()
            dispatch(removeLoader(LOADER))
          })
          .catch(() => {
            dispatch(removeLoader(LOADER))
            dispatch(addError({
              key: ERROR,
              message: constants.errorMessages.addingPriceAlert,
            }))
          })
        })
    }

    return payload
  }, [dispatch, isAuthenticated, getActivePriceAlerts])

  const onRemovePriceAlert = useCallback(async (vehicleId: number) => {
    const LOADER = LOADERS.removingPriceAlert
    const ERROR = ERRORS.removingPriceAlert

    if (isAuthenticated) {
      dispatch(addLoader(LOADER))
      dispatch(removeError(ERROR))

      await fetchAuthSession()
        .then(data => {
          const token = data.tokens?.idToken?.toString()

          removePriceAlertService({
            vehicleId,
            token,
          })
          .then(() => {
            dispatch(
              postAnalyticsEvent({
                event: constants.eventTypes.removePriceAlert,
              }),
            )
            dispatch(removePriceAlert(vehicleId))
            dispatch(removeLoader(LOADER))
          })
          .catch(() => {
            dispatch(removeLoader(LOADER))
            dispatch(addError({
              key: ERROR,
              message: constants.errorMessages.removePriceAlert,
            }))
          })
        })
      }

  }, [dispatch, isAuthenticated])

  const getDealsForPriceAlert = useCallback(async (priceAlert: PriceAlert) => {
    const LOADER = LOADERS.fetchingDealsForPriceAlert
    const ERROR = ERRORS.fetchingDealsForPriceAlert

    await getDealsForPriceAlertService({
      leaseType: priceAlert.leaseType,
      mileage: priceAlert.mileage,
      term: priceAlert.term,
      initialPaymentInMonths: priceAlert.initialPaymentInMonths,
      vehicleId: priceAlert.vehicleId,
      sorting: 2,
    }).then(response => {
      dispatch(
        addPriceAlertWithDealsAndVehicleList({ priceAlert, results: response.results, vehicle: response.vehicle }),
      )
      dispatch(removeLoader(LOADER))
    }).catch(() => {
      dispatch(removeLoader(LOADER))
      dispatch(addError({
        key: ERROR,
        message: 'There has been an issue finding deals for your price alert. Please try again later.',
      }))
    })
  }, [dispatch])

  const getVehiclesForExpiredPriceAlert = useCallback(async (priceAlert: PriceAlert) => {
    const LOADER = LOADERS.fetchingDealsForPriceAlert
    const ERROR = ERRORS.fetchingDealsForPriceAlert

    await getDealsForPriceAlertService({
      leaseType: priceAlert.leaseType,
      mileage: priceAlert.mileage,
      term: priceAlert.term,
      initialPaymentInMonths: priceAlert.initialPaymentInMonths,
      vehicleId: priceAlert.vehicleId,
      sorting: 2,
    }).then(response => {
      dispatch(
        addExpiredPriceAlertWithDealsAndVehicleList({ priceAlert, results: response.results, vehicle: response.vehicle }),
      )
      dispatch(removeLoader(LOADER))
    }).catch(() => {
      dispatch(removeLoader(LOADER))
      dispatch(addError({
        key: ERROR,
        message: 'There has been an issue finding deals for your price alert. Please try again later.',
      }))
    })
  }, [dispatch])

  const onUpdatePriceAlertList = useCallback(() => {
    if (priceAlertList.length > 0) {
      priceAlertList.map((priceAlert: PriceAlert) => {
        getDealsForPriceAlert(priceAlert)
      })
    }
  }, [getDealsForPriceAlert, priceAlertList])

  return {
    getActivePriceAlerts,
    getExpiredPriceAlerts,
    doesPriceAlertExist,
    priceAlertDifferencesToCurrentLeaseProfile,
    onCreatePriceAlert,
    onRemovePriceAlert,
    getDealsForPriceAlert,
    getVehiclesForExpiredPriceAlert,
    onUpdatePriceAlertList,
  }
}
